import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AIChatInterface } from '../chatbot/AIChatInterface';
import { useAuth } from '../../hooks/useAuth';
import { useSiteConfig } from '../../hooks/useSiteConfig';

interface FloatingChatWidgetProps {
  className?: string;
}

export const FloatingChatWidget: React.FC<FloatingChatWidgetProps> = ({ className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [preservedMessageInput, setPreservedMessageInput] = useState('');
  const widgetRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { config } = useSiteConfig();
  const isChatWidgetEnabled = config?.features?.popupAiChatButtonEnabled ?? false;

  // Don't render the floating widget on AI chat pages, messages page, or Conductor page
  const isOnAIChatPage = location.pathname === '/ai-chat' || location.pathname === '/patients/ai-chat';
  const isOnMessagesPage = location.pathname === '/messages' || location.pathname === '/patients/messages';
  const isOnConductorPage = location.pathname.startsWith('/tools/conductor') || location.pathname.startsWith('/onecare/tools/conductor');
  
  // Reset widget state when authentication status changes
  useEffect(() => {
    if (!isAuthenticated) {
      setIsExpanded(false);
      setIsMinimized(false);
      setUnreadCount(0);
      setHasNewMessage(false);
    }
  }, [isAuthenticated]);

  if (!isChatWidgetEnabled) {
    return null;
  }

  // Removed auto-minimize timer to prevent unwanted collapses

  // Removed auto-minimize on click outside to prevent accidental collapses

  const handleToggleExpanded = () => {
    if (!isAuthenticated) {
      navigate('/onecare/login');
      return;
    }

    if (isExpanded) {
      setIsExpanded(false);
      setIsMinimized(false);
    } else {
      setIsExpanded(true);
      setIsMinimized(false);
      setUnreadCount(0);
      setHasNewMessage(false);
    }
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    setHasNewMessage(false);
  };

  const handleMaximize = () => {
    setIsMinimized(false);
    setUnreadCount(0);
    setHasNewMessage(false);
  };

  const handleGoToFullChat = () => {
    navigate('/onecare/ai-chat');
  };

  const handleNewMessage = () => {
    if (isMinimized || !isExpanded) {
      setUnreadCount(prev => prev + 1);
      setHasNewMessage(true);
    }
  };

  const getUserTypeFromRole = (): 'patient' | 'staff' | 'visitor' => {
    if (!user?.role_specialty) return 'patient';
    
    const roleGroup = user.role_specialty.role_group_name?.toLowerCase() || '';
    const roleSpecialty = user.role_specialty.name?.toLowerCase() || '';
    const roleDisplay = user.role_display?.toLowerCase() || '';
    
    // Check if user is healthcare staff
    if (roleGroup.includes('medical') || roleGroup.includes('nursing') || 
        roleGroup.includes('administrative') || roleGroup.includes('support') ||
        roleDisplay.includes('doctor') || roleDisplay.includes('nurse') || 
        roleDisplay.includes('admin') || roleDisplay.includes('staff') ||
        roleSpecialty.includes('physician') || roleSpecialty.includes('surgeon')) {
      return 'staff';
    }
    
    // Check if user is a visitor (no role specialty indicates visitor)
    if (!user.role_specialty || roleDisplay === 'visitor') {
      return 'visitor';
    }
    
    // Default to patient
    return 'patient';
  };

  if (isOnAIChatPage || isOnMessagesPage || isOnConductorPage) {
    return null;
  }

  // Don't render if user is not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div ref={widgetRef} className={`fixed bottom-4 right-20 z-50 ${className}`}>
      {/* Expanded Chat Interface */}
      {isExpanded && (
        <div 
          className={`mb-20 w-96 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden transition-all duration-300 ${
            isMinimized ? 'h-14' : 'h-[500px]'
          }`}
        >
          {/* Chat Header */}
          <div className="bg-blue-600 text-white p-3 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <h3 className="font-medium text-sm">AI Assistant</h3>
              {unreadCount > 0 && !isMinimized && (
                <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={handleGoToFullChat}
                className="p-1 hover:bg-blue-700 rounded transition-colors"
                title="Open full chat page"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </button>
              <button
                onClick={isMinimized ? handleMaximize : handleMinimize}
                className="p-1 hover:bg-blue-700 rounded transition-colors"
                title={isMinimized ? "Maximize" : "Minimize"}
              >
                {isMinimized ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1 hover:bg-blue-700 rounded transition-colors"
                title="Close chat"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Chat Content */}
          {!isMinimized && (
            <div className="h-[456px]">
              <AIChatInterface
                userType={getUserTypeFromRole()}
                context={{
                  user_id: user?.id,
                  user_role: user?.role_display,
                  role_specialty: user?.role_specialty?.name,
                  role_group: user?.role_specialty?.role_group_name,
                  doctor_rank: user?.doctor_rank?.name,
                  hierarchy_level: user?.hierarchy_level,
                  can_supervise: user?.can_supervise,
                  hospital_department: user?.primary_department?.name,
                  primary_unit: user?.primary_unit?.name,
                  current_unit: user?.current_unit,
                  current_shift: user?.current_shift,
                  employee_id: user?.employee_id,
                  license_number: user?.license_number,
                  specialties: user?.specialties,
                  full_name: user?.full_name,
                  timestamp: new Date().toISOString(),
                  widget_mode: true
                }}
                className="h-full"
                onNewMessage={handleNewMessage}
                widgetMode={true}
                messageInput={preservedMessageInput}
                onMessageInputChange={setPreservedMessageInput}
              />
            </div>
          )}

          {/* Minimized State Info */}
          {isMinimized && (
            <div className="px-3 py-2 text-sm text-gray-900 flex items-center justify-between">
              <span>AI Assistant</span>
              {hasNewMessage && (
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-blue-600">New message</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Floating Action Button */}
      <button
        onClick={handleToggleExpanded}
        className={`w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-all duration-200 hover:scale-105 flex items-center justify-center relative ${
          isExpanded ? 'bg-blue-700' : ''
        }`}
        title={isExpanded ? 'Close AI Assistant' : 'Open AI Assistant'}
      >
        {/* Unread Badge */}
        {(unreadCount > 0 || hasNewMessage) && !isExpanded && (
          <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount || '!'}
          </div>
        )}
        
        {isExpanded ? (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        )}
      </button>
    </div>
  );
};